import dayjs from 'dayjs';
import React, {useMemo} from 'react';
import {Box} from '@mui/material';
import {Body, Avatar, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {SwapMatch} from '@/types/swapMatch';
import styles from './InstagramPostPreview.module.scss';

interface InstagramPostPreviewProps {
  post: SwapMatch;
  creatorName: string;
  creatorProfileImage?: string;
  creatorHandle: string;
  brandName?: string;
}

function InstagramPostPreview({
  post,
  creatorName,
  creatorProfileImage = '',
  creatorHandle,
  brandName,
}: InstagramPostPreviewProps) {
  // Format post timestamp
  const postTimestamp = useMemo(() => {
    if (!post.postedAt) return '';
    return dayjs(post.postedAt).fromNow();
  }, [post.postedAt]);

  return (
    <Box className={styles.instagramPostPreview}>
      {/* Instagram Post Header */}
      <Box className={styles.postHeader}>
        <Box className={styles.postHeaderLeft}>
          <Avatar
            size="sm"
            src={creatorProfileImage}
            type={creatorProfileImage ? 'picture' : 'initials'}
            initials={creatorName
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
            variant="circular"
          />
          <Box className={styles.postHeaderText}>
            <Body size="sm" className={styles.postUsername}>
              {creatorHandle}
              {brandName && (
                <>
                  {' '}
                  {translate('and')} {brandName}
                </>
              )}
            </Body>
          </Box>
        </Box>
      </Box>

      {/* Instagram Post Image */}
      <Box className={styles.postImageContainer}>
        {post.thumbnailUrl ? (
          <img
            src={post.thumbnailUrl}
            alt={post.body || 'Post'}
            className={styles.postImage}
          />
        ) : (
          <Box className={styles.noPostImage} />
        )}
      </Box>

      {/* Instagram Post Footer */}
      <Box className={styles.postFooter}>
        {/* Caption */}
        {post.body && (
          <Box className={styles.postCaption}>
            <Body size="sm">
              <strong>{creatorHandle}</strong> {post.body}
            </Body>
          </Box>
        )}

        {/* View comments */}
        {post.commentCount > 0 && (
          <Body
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
            className={styles.viewComments}
          >
            {translate('View all')} {post.commentCount}{' '}
            {post.commentCount === 1
              ? translate('comment')
              : translate('comments')}
          </Body>
        )}

        {/* Timestamp */}
        {postTimestamp && (
          <Body
            size="xs"
            color={designSystemToken('semantic.fg.secondary')}
            className={styles.postTimestamp}
          >
            {postTimestamp}
          </Body>
        )}
      </Box>
    </Box>
  );
}

export default InstagramPostPreview;
